import { useState, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import type { TalentProfile } from '../types';
import { mapDbCandidateToTalentProfile } from '../lib/mappers';
import { useOrganization } from './useOrganization';

export const useShare = () => {
    const { org } = useOrganization();
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Generate a share link for a candidate
    const generateLink = useCallback(async (candidateId: string, expiresInDays = 7) => {
        setIsLoading(true);
        setError(null);
        try {
            // 1. Generate a generic token (UUID)
            // Note: In a real scenario, we might let the DB generate it, but here we generate it client-side
            // or we insert and let DB return it. Let's let DB generate it for security/uniqueness if we wanted, 
            // but the current schema allows inserting. Ideally we insert without token and get it back.
            // But to match current logic: 
            const token = self.crypto.randomUUID();

            // 2. Insert into Supabase
            const expiresAt = new Date();
            expiresAt.setDate(expiresAt.getDate() + expiresInDays);

            const { error: insertError } = await supabase
                .from('candidate_shares')
                .insert({
                    candidate_id: parseInt(candidateId),
                    token: token,
                    expires_at: expiresAt.toISOString(),
                    organization_id: org?.id,
                });

            if (insertError) throw insertError;

            // 3. Return the full URL
            const baseUrl = window.location.origin;
            return `${baseUrl}?token=${token}`;

        } catch (err: any) {
            console.error("Error generating link:", err);
            setError(err.message || "Failed to generate link");
            return null;
        } finally {
            setIsLoading(false);
        }
    }, [org]);

    // Retrieve candidate from token
    const getCandidateFromToken = useCallback(async (token: string): Promise<TalentProfile | null> => {
        setIsLoading(true);
        setError(null);
        try {
            // Call the secure RPC function. 
            // We pass token as string, the SQL function `get_shared_candidate(token_input text)` handles it.
            const { data, error } = await supabase
                .rpc('get_shared_candidate', { token_input: token });

            if (error) throw error;
            if (!data) throw new Error("Lien invalide ou expiré");

            // Map the returned data to TalentProfile
            return mapDbCandidateToTalentProfile(data as any);

        } catch (err: any) {
            console.error("Error fetching shared candidate:", err);
            setError(err.message || "Impossible de charger le profil");
            return null;
        } finally {
            setIsLoading(false);
        }
    }, []);

    return {
        generateLink,
        getCandidateFromToken,
        isLoading,
        error
    };
};
